import { useParams } from "react-router-dom";

import {
  tierHistoryOf,
  toolUsageOf,
  type McpTenantDetail,
  type McpTenantList,
  type McpTierHistory,
  type McpUsage,
} from "../../api/mcp";
import { Blurred } from "../../components/Blurred";
import { BlockControl } from "../../components/mcp/BlockControl";
import { TierControl } from "../../components/mcp/TierControl";
import { RecentChanges } from "../../components/audit/RecentChanges";
import { Reason } from "../../components/write/ReasonText";
import { Breadcrumbs } from "../../components/Layout";
import {
  CouldNotLook,
  Denied,
  LoadFailed,
  Loading,
  NotSeenRecently,
  SignedOut,
} from "../../components/states";
import { Badge, Card, Empty, Field, Fields, Notice, Page, Panel, Stat, StatRow, Table, Td, Th } from "../../components/ui";
import type { AppConfig } from "../../config";
import { Button } from "../../f7";
import { useResource } from "../../hooks/useResource";
import { ProductTag, StoredAs } from "../../products/PerProduct";
import {
  LABEEB_CATALOG,
  LABEEB_TIERS,
  effectiveDomainsOf,
  isTesterReport,
  labeebFeedbackOf,
  labeebTenantFeedbackPath,
  labeebTenantPath,
  labeebTenantQuotaPath,
  labeebTenantSettingsPath,
  domainTurnsTodayOf,
  labeebTenantUsagePath,
  labeebTenantUsersPath,
  labeebUserLabel,
  labeebUsersOf,
  tierDisplayName,
  tierRowsOf,
  type LabeebCatalog,
  type LabeebFeedback,
  type LabeebTenantDetail,
  type LabeebTenantQuota,
  type LabeebTenantSettings,
  type LabeebTiers,
  type LabeebUsage as LabeebUsageShape,
  type LabeebUsers,
} from "../../api/labeeb";
import { LABEEB_LIMIT_KEYS, limitLabel, limitValue } from "../policy/Tiers";
import { LabeebQuotaControl } from "../../components/labeeb/LabeebQuotaControl";
import { LabeebRules } from "../../components/labeeb/LabeebRules";
import { LabeebStandingControl } from "../../components/labeeb/LabeebStandingControl";

export function Tenant({ config }: { config: AppConfig }) {
  const { ref = "" } = useParams();
  const enc = encodeURIComponent(ref);

  const tenant = useResource<McpTenantDetail>(config, `/mcp/admin/tenant/${enc}`, [ref]);
  const usage = useResource<McpUsage>(config, `/mcp/admin/tenant/${enc}/usage`, [ref]);
  const history = useResource<McpTierHistory>(config, `/mcp/admin/tenant/${enc}/history`, [ref]);
  // The REAL list type, not an inline `{ known_tiers: string[] }` — that
  // shape claimed the field is always present, and on a `{}` response
  // TierControl then called `.filter` on undefined and took the page down.
  const list = useResource<McpTenantList>(config, "/mcp/admin/tenants");

  // Labeeb's view of the SAME merchant. Both products key on the business
  // reference, so one id reaches both — which is what makes a tier split
  // ("Pro in one product, Plus in the other") visible at last.
  //
  // A 404 here is not an error to shout about: Labeeb holds a config item only
  // for merchants somebody has explicitly configured, and its absence means
  // "on defaults". The panel below renders that state rather than a failure.
  const labeeb = useResource<LabeebTenantDetail>(config, labeebTenantPath(ref), [ref]);
  const labeebCatalog = useResource<LabeebCatalog>(config, LABEEB_CATALOG);

  const s = tenant.state;

  return (
    <Page title={<span className="ref">{ref}</span>}>
      <Breadcrumbs
        trail={[
          { label: "Overview", to: "/" },
          { label: "Tenants", to: "/tenants" },
          { label: ref },
        ]}
      />

      {s.kind === "loading" && <Loading />}
      {s.kind === "signedout" && <SignedOut />}
      {s.kind === "denied" && <Denied message={s.error.message} />}
      {s.kind === "unavailable" && (
        <CouldNotLook what="this merchant" message={s.error.message} onRetry={tenant.reload} />
      )}
      {s.kind === "error" && <LoadFailed what="this merchant" message={s.error.message} />}

      {/* A 404 still offers the operation that WORKS on a 404 merchant.
          Blocking is keyed by reference and never needs the ledger. */}
      {s.kind === "notfound" && (
        <NotSeenRecently
          reference={ref}
          actions={
            <Button outline href={`/tenants/${enc}/users`}>
              Manage blocks for this reference
            </Button>
          }
        />
      )}

      {s.kind === "ready" &&
        (() => {
          const d = s.data;
          const u = usage.state.kind === "ready" ? usage.state.data : null;
          const h = history.state.kind === "ready" ? history.state.data : null;

          // `blocklist: {blocked, matched}` is GONE from this response — MCP
          // flattened it to `blocked` + `blocked_since`/`blocked_by`/
          // `blocked_reason` (3e43b98). Same three facts the old `matched.
          // merchant_wide` row carried, under their new names.
          const wide = d.blocked
            ? { reason: d.blocked_reason, added_by: d.blocked_by, added_at: d.blocked_since }
            : null;

          // Labeeb's key for this merchant, when Labeeb holds one at all. A
          // 404 from Labeeb is the normal "no config item" case, not an
          // error, so this stays null rather than rendering an empty row.
          const labeebTenantId =
            labeeb.state.kind === "ready" ? (labeeb.state.data?.tenant_id ?? null) : null;

          return (
            // The Page column already owns the rhythm at `gap-6`; `space-y-5`
            // here set a second, different one, so the tenant record was the
            // only page whose blocks sat closer together than the rest.
            <div className="flex flex-col gap-6">
              {/* The design's IDENTIFIER STRIP: a compact filled band of
                  labelled keys, not rows in the same list as the controls. The
                  identifiers are what you copy into a ticket; the tier picker
                  and the block button are what you act with, and interleaving
                  them in one `dl` made a two-line field list eight rows long
                  and buried both. */}
              <div className="flex flex-wrap gap-x-8 gap-y-3 rounded-lg bg-slate-50 p-4 ring-1 ring-slate-950/5 dark:bg-white/5 dark:ring-white/10">
                {([
                  ["Name", d.business_name, false],
                  ["business_reference", d.business_reference, true],
                  ["Owner email", d.email, true],
                  // Labeeb's own key, shown only when Labeeb knows this
                  // merchant. An operator who arrived from that side holds
                  // this id and nothing else, and it need not equal the
                  // business reference — so a blank row here would read as
                  // "Labeeb has no id" rather than "Labeeb has no merchant".
                  //
                  // `business_id` is deliberately NOT here: it is a UUID,
                  // searchable but never rendered. See `api/mcp.ts`.
                  ...(labeebTenantId
                    ? [["Labeeb tenant_id", labeebTenantId, true] as const]
                    : []),
                ] as const).map(([label, value, mono]) => (
                  <div key={label as string} className="min-w-0">
                    <p className="text-[10px] font-semibold tracking-wider text-slate-400 uppercase dark:text-slate-500">
                      {label as string}
                    </p>
                    <p
                      className={`mt-0.5 text-sm text-slate-950 dark:text-white ${mono ? "font-mono" : ""}`}
                      dir="auto"
                    >
                      {value as string}
                    </p>
                  </div>
                ))}
              </div>

              {/* ONE BLOCK PER PRODUCT, each self-contained and closed.
                  A reader debugging "MCP is wrong for this merchant" opens one
                  block and finds its tier, access, usage, caps and history
                  together, rather than picking them out of a pile of cards
                  interleaved with the other product's.

                  Each block still shows BOTH tiers in its Plan section — a
                  merchant on Pro in one product and Plus in the other is a
                  `tier split`, and splitting the page by product is exactly
                  what would hide it. */}
              <Card title="MCP" aside={<ProductTag id="mcp" />} note="Tier, access, usage and caps" collapsible>
              <div className="grid gap-4 lg:grid-cols-2">
                <Card title="Plan tier" aside={<ProductTag id="mcp" />}>
                  <Fields>
                    <Field label="MCP">
                      <Badge tone={d.tier ? "neutral" : "amber"}>{d.effective_tier}</Badge>
                      {/* `tier: null` means nothing is stored. The server
                          treats them as free, but reporting the stored value
                          honestly is the point — a GET never creates the row. */}
                      {d.tier_is_implicit ? (
                        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                          Nothing is stored for this merchant. The server treats them as{" "}
                          <span className="ref">{d.effective_tier}</span>; reading this page
                          does not create the row.
                        </p>
                      ) : (
                        d.tier_source && (
                          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                            Stored as <span className="ref">{d.tier}</span>, set by{" "}
                            <span className="ref">{d.tier_source.replace(/^admin:/, "")}</span>
                            {d.tier_updated_at &&
                              ` on ${new Date(d.tier_updated_at).toLocaleDateString()}`}
                          </p>
                        )
                      )}
                    </Field>
                    {/* NO Labeeb row here. A block titled MCP that reports
                        Labeeb's state is the same mistake as a column reading
                        "Labeeb not wired" on every row: the reader came for
                        one product and got a line about the other.

                        The tier SPLIT is still visible — Labeeb's own block
                        renders both values, which is one place rather than
                        two saying it. */}
                  </Fields>

                  <div className="mt-4 flex flex-col gap-3">
                    <TierControl
                      config={config}
                      reference={d.business_reference}
                      currentTier={d.effective_tier}
                      knownTiers={
                        list.state.kind === "ready" ? (list.state.data.known_tiers ?? []) : []
                      }
                      onDone={() => {
                        tenant.reload();
                        history.reload();
                      }}
                    />
                  </div>
                </Card>

                <Card title="Access" aside={<ProductTag id="mcp" />}>
                  <Fields>
                    <Field label="MCP">
                      {d.blocked ? (
                        <Badge tone="red">Blocked</Badge>
                      ) : (
                        <Badge tone="green">Active</Badge>
                      )}
                      {wide && (
                        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                          {wide.reason && (
                            <>
                              Reason: <Reason text={wide.reason} />.{" "}
                            </>
                          )}
                          {wide.added_by && (
                            <>
                              Blocked by <span className="ref">{wide.added_by}</span>
                            </>
                          )}
                          {wide.added_at && ` on ${new Date(wide.added_at).toLocaleDateString()}`}
                        </p>
                      )}
                    </Field>

                  </Fields>

                  {/* The caveat belongs to THIS control, not to a row about
                      the other product: blocking here stops MCP tool calls,
                      and says nothing about the assistant. */}
                  <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                    Blocking stops MCP tool calls only. A merchant blocked here can still
                    be talking to the assistant.
                  </p>

                  <div className="mt-4 flex flex-wrap items-center gap-2">
                    <BlockControl
                      config={config}
                      scope={{ kind: "merchant", reference: d.business_reference }}
                      blocked={d.blocked}
                      onDone={tenant.reload}
                    />
                    <Button plain href={`/tenants/${enc}/users`}>
                      Blocked users
                    </Button>
                  </div>
                </Card>
              </div>

              {/* The panel's action belongs in its HEADER, which is the slot
                  Card gives it and the slot the design uses. As a `justify-end`
                  row above the body it pushed the figures down by its own
                  height and lined up with nothing. */}
              <Card
                title="Today"
                aside={
                  <>
                    <Button plain href={`/tenants/${enc}/usage`}>
                      All usage
                    </Button>
                    <ProductTag id="mcp" />
                  </>
                }
              >
                <StatRow>
                  <Stat value={u?.total_credits ?? "…"} label="Credits today" to={`/tenants/${enc}/usage`} />
                  <Stat
                    value={u ? Object.keys(toolUsageOf(u)).length : "…"}
                    label="Tools used"
                    to={`/tenants/${enc}/usage`}
                  />
                  <Stat value={u?.day ?? "…"} label="UTC day" hint={u?.day_boundary} />
                </StatRow>
              </Card>

              {/* "Effective caps" used to live here, joined to the catalog's
                  resolver warnings. Both `caps` and `unprotected_resolvers`
                  are GONE from this response (3e43b98) — caps live on
                  GET /admin/quota/{tier} now, which is exactly the page this
                  card's own "Tier caps" button already sent someone to. */}
              <Card
                title="Caps"
                note={
                  <>
                    Caps for tier <span className="ref">{d.effective_tier}</span> live on the
                    tier's own page now
                    {d.tier_is_implicit && " (implicit — no tier is stored)"}.
                  </>
                }
                aside={<ProductTag id="mcp" />}
              >
                <Button href={`/policy/caps/${encodeURIComponent(d.effective_tier)}`}>
                  Open {d.effective_tier} caps
                </Button>
              </Card>

              <Card
                title="Recent tier changes"
                aside={
                  <>
                    <Button plain href={`/tenants/${enc}/history`}>
                      Full history
                    </Button>
                    <ProductTag id="mcp" />
                  </>
                }
              >
                {tierHistoryOf(h).length > 0 ? (
                  <Table head={<tr><Th>Tier</Th><Th>Set by</Th><Th>When</Th></tr>}>
                    {tierHistoryOf(h).slice(0, 3).map((e, i) => (
                      <tr key={i}>
                        <Td>
                          <Badge>{e.tier}</Badge>
                        </Td>
                        <Td>
                          {e.source === null ? (
                            <span className="text-xs text-slate-500 dark:text-slate-400">unknown</span>
                          ) : e.source === "signup" ? (
                            <span className="text-xs text-slate-500 dark:text-slate-400">automatic — first use</span>
                          ) : (
                            <span className="ref text-xs">{e.source.replace(/^admin:/, "")}</span>
                          )}
                        </Td>
                        <Td>
                          <span className="text-xs text-slate-500 dark:text-slate-400">
                            {e.created_at ? new Date(e.created_at).toLocaleDateString() : "—"}
                          </span>
                        </Td>
                      </tr>
                    ))}
                  </Table>
                ) : (
                  <Empty>No recorded changes.</Empty>
                )}
              </Card>
              </Card>{/* end MCP block */}
            </div>
          );
        })()}

      {/* OUTSIDE the MCP gate on purpose — see LabeebRecord. */}
      <div className="mt-6">
        <LabeebRecord
          config={config}
          tenantId={ref}
          labeeb={labeeb}
          labeebCatalog={labeebCatalog}
          mcpTier={s.kind === "ready" ? (s.data.tier ?? null) : null}
        />
      </div>

      <div className="mt-4">
        <RecentChanges config={config} domain="tenant" target={ref} />
      </div>

    </Page>
  );
}

/**
 * Labeeb's half of the record, rendered INDEPENDENTLY of MCP's.
 *
 * It used to sit inside `mcpState.kind === "ready"`, so a merchant Labeeb
 * knows and MCP does not — which /tenants now lists, and which is the normal
 * case for a merchant that has never used a tool — showed "Not seen recently"
 * and nothing else. Everything Labeeb held about them was hidden behind
 * another product's 404.
 */
function LabeebRecord({
  config,
  tenantId,
  labeeb,
  labeebCatalog,
  mcpTier,
}: {
  config: AppConfig;
  tenantId: string;
  labeeb: ReturnType<typeof useResource<LabeebTenantDetail>>;
  labeebCatalog: ReturnType<typeof useResource<LabeebCatalog>>;
  /** MCP's stored tier, or null when MCP has never seen this merchant. */
  mcpTier: string | null;
}) {
  const ref = tenantId;

  // Has this merchant ever actually RUN Labeeb? `has_config_item` does not
  // answer that — see `labeebUsage`. Until we know they have, the block reads
  // and does not write: the tier/agent PATCH is itself what creates the
  // TenantConfigs row that makes them a Labeeb tenant, so offering it on an
  // MCP-only merchant enrolled them the moment anyone looked.
  //
  // Read while the resource is still loading as "used", not "unseen": showing
  // the enrolment prompt for a beat and then swapping it for the editor would
  // flash the wrong answer at the operator on every page load.
  // `has_config_item` is gone: a business is in Labeeb's list or it is not,
  // and a detail read either resolves or 404s. "Never used Labeeb" is no
  // longer something the console can infer from a field, so it does not
  // claim to — a merchant Labeeb returns is treated as known.

  /* Labeeb's half. This was five hardcoded <Blurred> panels whose
              note said no Labeeb Lambda published a lambda_arn — true when
              it was written, false since the gateway mounted /labeeb/*.
              Worse, being hand-rolled Blurred rather than ProductPanel, it
              bypassed the switch that de-blurs Labeeb everywhere else, so
              it stayed blurred no matter what the registry said.

              Three of the five are now real. HITL keeps its blur because
              it is genuinely unbacked: there is no hitl route among the
              sixteen. `stable_agents` is gone from here — it is app-level,
              not per-tenant, and has a real editor under Settings. */
  return (
          <Card
            title="Labeeb"
            aside={<ProductTag id="labeeb" />}
            note="Tier, the envelope it grants, capabilities and usage"
            collapsible
          >
            <div className="flex flex-col gap-4">
              <Card title="Plan tier" aside={<ProductTag id="labeeb" />}>
                <Fields>
                  <Field label="Labeeb">
                    <LabeebTier resource={labeeb} />
                  </Field>
                  {/* THE one place both tiers appear. MCP's block reports
                      only MCP, so this is where a tier split — Pro in one
                      product, Plus in the other — stays visible. */}
                  <Field label="Stored as">
                    <StoredAs
                      stored={{
                        mcp: mcpTier,
                        // The tier moved to its own document
                        // (`GET /admin/tenant/{id}/quota`); the detail read
                        // cannot answer this, and guessing from status would
                        // be worse than saying nothing.
                        labeeb: undefined,
                      }}
                    />
                  </Field>
                </Fields>

                {/* The tier is its own document now, with its own version,
                    so it gets its own editor reading and writing
                    `/admin/tenant/{id}/quota`. The enrolment gate that used
                    to sit here is gone: it existed because a Labeeb PATCH
                    silently CREATED a TenantConfigs row, enrolling a merchant
                    by being looked at. The new API has no such side effect —
                    a quota write names a business that already exists, and
                    404s when it does not. */}
                <div className="mt-4">
                  <LabeebQuotaControl config={config} tenantId={ref} catalog={labeebCatalog} />
                </div>
              </Card>

              <LabeebEnvelope config={config} tenantId={ref} catalog={labeebCatalog} />

              <LabeebCapabilities config={config} tenantId={ref} />

              <LabeebRules config={config} tenantId={ref} />

              <LabeebUsage config={config} tenantId={ref} />

              <LabeebUsersPanel config={config} tenantId={ref} />

              <LabeebFeedbackPanel config={config} tenantId={ref} />

              <Card title="Access" aside={<ProductTag id="labeeb" />}>
                {/* NO LONGER NotWired. `GET`/`PATCH /admin/tenant/{id}/standing`
                    exists, and a suspended business has its NEXT request
                    refused — real enforcement, unlike the `LabeebUserMgmt`
                    stack's status label that the agent path never read.
                    The same control is on /policy/access, deliberately: one
                    write, two doors, matching how MCP blocking already
                    works in this console. */}
                <LabeebStandingControl config={config} tenantId={ref} />
              </Card>

              {/* The ONLY one of the five still genuinely unbacked: no
                  `hitl` route exists among Labeeb's sixteen. */}
              <Blurred what="HITL gates · this tenant" needs="a gates route on Labeeb's admin API" minHeight={140}>
                <Panel className="p-4">
                  <div className="flex flex-col gap-3">
                    {Array.from({ length: 4 }, (_, i) => (
                      <div key={i} className="flex items-center justify-between gap-3">
                        <div className="h-3 w-28 rounded bg-slate-950/10 dark:bg-white/10" />
                        <div className="h-6 w-10 rounded-full bg-slate-950/10 dark:bg-white/10" />
                      </div>
                    ))}
                  </div>
                </Panel>
              </Blurred>
            </div>
          </Card>
  );
}

/**
 * Labeeb's tier for this merchant, or an honest account of why there isn't one.
 *
 * `has_config_item: false` is the NORMAL state — a merchant nobody has
 * explicitly configured has no row at all, and the server treats it as the
 * default tier. Rendering that as an error, or as a blank, would both be
 * wrong: the first invents a fault, the second invents a gap.
 *
 * A 404 means the same thing operationally (Labeeb knows nothing about this
 * id), so it is rendered the same way rather than as a failure.
 */
function LabeebTier({
  resource,
}: {
  resource: ReturnType<typeof useResource<LabeebTenantDetail>>;
}) {
  const state = resource.state;

  if (state.kind === "loading") {
    return <span className="text-xs text-slate-500 dark:text-slate-400">Loading…</span>;
  }
  if (state.kind === "signedout") {
    return <span className="text-xs text-slate-500 dark:text-slate-400">Signed out</span>;
  }
  if (state.kind === "denied") {
    return (
      <span className="text-xs text-slate-500 dark:text-slate-400">
        You cannot read Labeeb's tenant config.
      </span>
    );
  }
  if (state.kind === "notfound") {
    return (
      <>
        <Badge tone="neutral">on defaults</Badge>
        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
          Labeeb holds no config for this merchant — no explicit tier, no agent
          narrowing. That is the normal state, not a gap.
        </p>
      </>
    );
  }
  if (state.kind === "unavailable" || state.kind === "invalid" || state.kind === "error") {
    // "Could not look" is NOT "there is nothing" — saying the merchant is on
    // defaults here would claim a fact we failed to read.
    return (
      <span className="text-xs text-amber-700 dark:text-amber-400">
        Could not read Labeeb's tier. {state.error.message}
      </span>
    );
  }

  const d = state.data;
  // THE TIER NO LONGER LIVES ON THIS ROUTE. `GET /admin/tenant/{id}` carries
  // identity and standing; the tier is its own document at
  // `GET /admin/tenant/{id}/quota`, with its own version to write against.
  // What this cell can honestly report from the detail read is standing.
  if (d?.status === "suspended") {
    return <Badge tone="red">suspended</Badge>;
  }

  return (
    <>
      <span className="text-xs text-slate-500 dark:text-slate-400">
        {d?.status === "active" ? "Active" : (d?.status ?? "unknown")}
      </span>
      <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
        Tier and domains are below — each is its own document now, with its own
        version.
      </p>
    </>
  );
}

/**
 * The envelope this merchant's Labeeb tier grants.
 *
 * `GET /labeeb/admin/tiers` holds the limits; the tenant's `tier_id` says
 * which row applies. This used to be a blurred panel with four dashes whose
 * labels — Daily messages, Monthly messages, Monthly tokens, Snowflake
 * queries — are a literal one-to-one match for the four documented limit keys.
 *
 * The columns come from the response, not a hardcoded list: a renamed or
 * trimmed limit key should shrink the panel rather than leave a row of
 * dashes behind.
 */
function LabeebEnvelope({
  config,
  tenantId,
  catalog,
}: {
  config: AppConfig;
  tenantId: string;
  catalog: ReturnType<typeof useResource<LabeebCatalog>>;
}) {
  const tiers = useResource<LabeebTiers>(config, LABEEB_TIERS);
  const cat = catalog.state.kind === "ready" ? catalog.state.data : null;

  // The tier comes from the QUOTA document now, not the tenant detail — that
  // route stopped carrying `tier_id` when Labeeb's admin API was replaced.
  const quota = useResource<LabeebTenantQuota>(config, labeebTenantQuotaPath(tenantId));
  const tierId = quota.state.kind === "ready" ? (quota.state.data?.tier_id ?? null) : null;
  const rows = tiers.state.kind === "ready" ? tierRowsOf(tiers.state.data) : [];
  const row = tierId ? rows.find((r) => r.tier_id === tierId) : undefined;

  return (
    <Card
      title={`Tier envelope${tierId ? ` · ${tierDisplayName(cat, tierId)}` : ""}`}
      aside={<ProductTag id="labeeb" />}
    >
      {tiers.state.kind === "loading" && <Loading what="the envelope" />}
      {tiers.state.kind === "signedout" && <SignedOut />}
      {tiers.state.kind === "denied" && <Denied message={tiers.state.error.message} />}
      {tiers.state.kind === "unavailable" && (
        <CouldNotLook
          what="the envelope"
          message={tiers.state.error.message}
          onRetry={tiers.reload}
        />
      )}
      {tiers.state.kind === "notfound" && (
        <LoadFailed what="the envelope" message="Route not found." />
      )}
      {(tiers.state.kind === "invalid" || tiers.state.kind === "error") && (
        <LoadFailed what="the envelope" message={tiers.state.error.message} />
      )}

      {tiers.state.kind === "ready" && !tierId && (
        <p className="text-sm text-slate-500 dark:text-slate-400">
          No explicit tier, so the runtime falls back to the default. The envelope
          it lands on is whichever tier <span className="ref">quota.default_tier_id</span>{" "}
          names.
        </p>
      )}

      {tiers.state.kind === "ready" && tierId && !row && (
        <Notice tone="amber" title="No row for this tier">
          The merchant is on <span className="ref">{tierId}</span>, which the tier
          table does not list. Nothing here is enforced against an envelope that
          does not exist.
        </Notice>
      )}

      {tiers.state.kind === "ready" && row && (
        <>
          {/* Two named integers now, not an open map of limit keys. */}
          <Fields>
            {LABEEB_LIMIT_KEYS.map((k) => (
              <Field key={k} label={limitLabel(k)}>
                <span className="tabular-nums">{limitValue(row, k)}</span>
              </Field>
            ))}
          </Fields>
        </>
      )}
    </Card>
  );
}

/**
 * "12 of 50" / "12" / "—", from the new flat counters.
 *
 * Replaces `describeCounter`, which read the old `{messages, limit}` object.
 * A count with no limit is real (an unlimited tier), and must not render as
 * "12 of undefined"; no count at all is a dash, never a zero — those say
 * different things to an operator chasing a quota complaint.
 */
function describeUsage(used: number | undefined, limit: number | undefined): string {
  if (typeof used !== "number") return "—";
  if (typeof limit !== "number" || limit < 0) return String(used);
  return `${used} of ${limit}`;
}

/**
 * Which DOMAINS this merchant may use.
 *
 * REPLACES the agents panel. Labeeb's new admin API has no agents at all —
 * the word does not appear once in its OpenAPI spec — and `enabled_domains`
 * on `GET /admin/tenant/{id}/settings` is what took their place.
 *
 * NOT itself collapsible. The product BLOCK is the collapsible unit; folding
 * its sections as well meant an operator who opened Labeeb saw one expanded
 * panel and four shut bars.
 *
 * The effective set is now READ, not derived. The old panel computed it by
 * intersecting the tenant's agents with an app-level ceiling and labelled the
 * result "derived", because no route returned it. `settings.effective` returns
 * it, per domain, with `restricted_by` naming whatever is holding one off —
 * so the console can finally say WHY a capability is unavailable instead of
 * only that it is.
 */
function LabeebCapabilities({
  config,
  tenantId,
}: {
  config: AppConfig;
  tenantId: string;
}) {
  const settings = useResource<LabeebTenantSettings>(
    config,
    labeebTenantSettingsPath(tenantId),
  );
  const s = settings.state;

  return (
    <Card title="Domains" aside={<ProductTag id="labeeb" />}>
      {s.kind === "loading" && <Loading what="domains" />}
      {s.kind === "signedout" && <SignedOut />}
      {s.kind === "denied" && <Denied message={s.error.message} />}
      {s.kind === "unavailable" && (
        <CouldNotLook what="domains" message={s.error.message} onRetry={settings.reload} />
      )}
      {s.kind === "notfound" && (
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Labeeb holds no settings document for this merchant, so every domain the
          environment allows applies.
        </p>
      )}
      {(s.kind === "invalid" || s.kind === "error") && (
        <LoadFailed what="domains" message={s.error.message} />
      )}

      {s.kind === "ready" &&
        (() => {
          const domains = effectiveDomainsOf(s.data);
          const asked = s.data?.settings?.enabled_domains;

          if (domains.length === 0) {
            return (
              <p className="text-sm text-slate-500 dark:text-slate-400">
                No domains are reported for this merchant.
              </p>
            );
          }

          return (
            <>
              <Table
                head={
                  <tr>
                    <Th>Domain</Th>
                    <Th>In force</Th>
                    <Th>Held off by</Th>
                  </tr>
                }
              >
                {domains.map((dm) => (
                  <tr key={dm.domain}>
                    <Td>
                      <span className="ref">{dm.domain}</span>
                    </Td>
                    <Td>
                      {dm.enabled ? (
                        <Badge>on</Badge>
                      ) : (
                        <Badge tone="red">off</Badge>
                      )}
                    </Td>
                    <Td>
                      {/* The field the agent model could never express. A
                          domain off because the ENVIRONMENT disabled it is a
                          different problem from one this merchant turned off,
                          and the fix lives in a different place. */}
                      {dm.restricted_by ? (
                        <span className="text-xs text-amber-700 dark:text-amber-400">
                          {dm.restricted_by}
                        </span>
                      ) : (
                        <span className="text-xs text-slate-400 dark:text-slate-500">—</span>
                      )}
                    </Td>
                  </tr>
                ))}
              </Table>

              <p className="mt-3 text-xs/5 text-slate-500 dark:text-slate-400">
                This table is what RUNS. What the merchant asked for is{" "}
                <span className="ref">
                  {asked === undefined
                    ? "unset"
                    : typeof asked === "string"
                      ? asked
                      : asked.length === 0
                        ? "none"
                        : asked.join(", ")}
                </span>
                {" — "}the environment can still hold a domain off, and the column above
                says when it does.
              </p>
            </>
          );
        })()}
    </Card>
  );
}

/**
 * Today and this month against the envelope.
 *
 * NOT a 30-day chart: neither product serves a usage-history route — MCP gives
 * the current UTC day and Labeeb today/this month — so the series the old
 * blurred panel drew was synthetic. Shipping the two real numbers beats
 * shipping a shape.
 *
 * `per_agent_today` is rendered apart from the enforced totals because it is
 * recorded but NOT enforced; folding it in would imply a limit nothing checks.
 */
function LabeebUsage({
  config,
  tenantId,
}: {
  config: AppConfig;
  tenantId: string;
}) {
  // The standalone route, which nothing called until now even though
  // labeebTenantUsagePath() has been exported all along. The tenant detail
  // also nests `usage`, but the dedicated route is the one that stays correct
  // if the detail response is ever trimmed.
  const usage = useResource<LabeebUsageShape>(
    config,
    labeebTenantUsagePath(tenantId),
    [tenantId],
  );
  // The detail route no longer bundles a usage object, so this read is the
  // only source. There is nothing left to fall back to.
  const u = usage.state.kind === "ready" ? usage.state.data : null;

  return (
    <Card title="Usage" aside={<ProductTag id="labeeb" />}>
      {usage.state.kind === "loading" && <Loading what="usage" />}
      {usage.state.kind === "signedout" && <SignedOut />}
      {usage.state.kind === "denied" && <Denied message={usage.state.error.message} />}
      {usage.state.kind === "unavailable" && (
        <CouldNotLook what="usage" message={usage.state.error.message} onRetry={usage.reload} />
      )}
      {usage.state.kind === "notfound" && (
        <p className="text-sm text-slate-500 dark:text-slate-400">
          No usage recorded for this merchant.
        </p>
      )}
      {(usage.state.kind === "invalid" || usage.state.kind === "error") && (
        <LoadFailed what="usage" message={usage.state.error.message} />
      )}

      {usage.state.kind === "ready" && (
        <>
          {/* Flat integers with their own limits now, instead of the old
              nested `{messages, limit}` objects — and the API serves the
              window boundary too, so the panel can say when the allowance
              returns rather than implying the limit is permanent. */}
          <Fields>
            <Field label="Today">{describeUsage(u?.messages_today, u?.daily_limit)}</Field>
            <Field label="This month">
              {describeUsage(u?.messages_this_month, u?.monthly_limit)}
            </Field>
            <Field label="Day resets">{u?.day_resets_at ?? "—"}</Field>
            <Field label="Month resets">{u?.month_resets_at ?? "—"}</Field>
          </Fields>
          {domainTurnsTodayOf(u).length > 0 && (
            <div className="mt-3">
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Turns per domain today — recorded, not enforced against any limit.
                Absent unless the usage read asks for a specific date.
              </p>
              <div className="mt-1 flex flex-wrap gap-2">
                {domainTurnsTodayOf(u).map((row) => (
                  <Badge key={row.domain}>
                    {row.domain}: {row.turns}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </Card>
  );
}

/**
 * The people provisioned in this tenant, owner first.
 *
 * Read-only: `GET /admin/tenant/{id}/users` has no write path, and there is
 * deliberately no fleet-wide equivalent — a list spraying every merchant's
 * staff names and emails is a different privacy posture from looking up the
 * tenant you are already supporting.
 */
function LabeebUsersPanel({ config, tenantId }: { config: AppConfig; tenantId: string }) {
  const users = useResource<LabeebUsers>(config, labeebTenantUsersPath(tenantId), [tenantId]);
  const state = users.state;

  return (
    <Card title="Users" aside={<ProductTag id="labeeb" />}>
      {state.kind === "loading" && <Loading what="users" />}
      {state.kind === "signedout" && <SignedOut />}
      {state.kind === "denied" && <Denied message={state.error.message} />}
      {state.kind === "unavailable" && (
        <CouldNotLook what="users" message={state.error.message} onRetry={users.reload} />
      )}
      {state.kind === "notfound" && (
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Labeeb has no users recorded for this merchant.
        </p>
      )}
      {(state.kind === "invalid" || state.kind === "error") && (
        <LoadFailed what="users" message={state.error.message} />
      )}

      {state.kind === "ready" &&
        (labeebUsersOf(state.data).length === 0 ? (
          <Empty>Nobody is provisioned in this tenant yet.</Empty>
        ) : (
          <>
            <Table
              head={
                <tr>
                  <Th>Person</Th>
                  <Th>Role</Th>
                  <Th>Standing</Th>
                  <Th>Last seen</Th>
                </tr>
              }
            >
              {labeebUsersOf(state.data).map((u) => (
                <tr key={u.user_id}>
                  <Td>
                    {/* name/email can still be absent on a row Labeeb has
                        seen but never resolved — fall back rather than blank. */}
                    <span className="ref">{labeebUserLabel(u)}</span>
                    {u.email && u.name && (
                      <span className="ml-2 text-xs text-slate-500 dark:text-slate-400">
                        {u.email}
                      </span>
                    )}
                  </Td>
                  <Td>{u.is_owner && <Badge tone="neutral">owner</Badge>}</Td>
                  <Td>
                    {/* REAL ENFORCEMENT, unlike the old `is_active` label.
                        A blocked user's next request is refused. */}
                    {u.status === "blocked" ? (
                      <div>
                        <Badge tone="red">blocked</Badge>
                        {(u.blocked_by || u.reason) && (
                          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                            {u.blocked_by ? `by ${u.blocked_by}` : ""}
                            {u.reason ? ` — "${u.reason}"` : ""}
                          </p>
                        )}
                      </div>
                    ) : (
                      <span className="text-xs text-slate-500 dark:text-slate-400">
                        {u.status ?? "—"}
                      </span>
                    )}
                  </Td>
                  <Td>
                    {/* `last_seen` is written by the REQUEST path, so it means
                        what its name says. The old `last_active_at` moved only
                        on chat create or delete — never on sending a message —
                        and was withheld for that reason. */}
                    <span className="text-xs text-slate-500 dark:text-slate-400">
                      {u.last_seen ? new Date(u.last_seen).toLocaleDateString() : "—"}
                    </span>
                  </Td>
                </tr>
              ))}
            </Table>

            {/* The UserStatsTable warning that stood here is gone with the
                data. Messages and Last active were withheld because their
                counter had lost its incrementing writer and only ever went
                down; the new API does not serve those fields at all, so there
                is nothing left to withhold or explain. */}
          </>
        ))}
    </Card>
  );
}

/**
 * What this merchant's users said about the answers they got.
 *
 * REPORTS, NOT RATINGS. This used to be a thumbs up/down with an overlapping
 * `tester` tally, a category and a severity. The new API serves a report with
 * a `source`, a `status`, a free-text `description`, and the thread and turn
 * it came from — so a report is traceable back to the conversation instead of
 * summarised as a sentiment. There is no summary to total any more.
 *
 * Read-only, permanently: a report is the user's own words, and an API that
 * could edit it would make every remaining row untrustworthy as evidence.
 */
function LabeebFeedbackPanel({ config, tenantId }: { config: AppConfig; tenantId: string }) {
  const feedback = useResource<LabeebFeedback>(
    config,
    labeebTenantFeedbackPath(tenantId),
    [tenantId],
  );
  const state = feedback.state;
  const items = state.kind === "ready" ? labeebFeedbackOf(state.data) : [];

  return (
    <Card title="Feedback" aside={<ProductTag id="labeeb" />}>
      {state.kind === "loading" && <Loading what="feedback" />}
      {state.kind === "signedout" && <SignedOut />}
      {state.kind === "denied" && <Denied message={state.error.message} />}
      {state.kind === "unavailable" && (
        <CouldNotLook what="feedback" message={state.error.message} onRetry={feedback.reload} />
      )}
      {state.kind === "notfound" && (
        <p className="text-sm text-slate-500 dark:text-slate-400">
          No feedback recorded for this merchant.
        </p>
      )}
      {(state.kind === "invalid" || state.kind === "error") && (
        <LoadFailed what="feedback" message={state.error.message} />
      )}

      {state.kind === "ready" && (
        <>
          {/* The "this page undercounts" notice is gone with the
              `complete` flag it read: the new payload is a plain cursor page
              with no partial-read signal, so claiming completeness either way
              would be an invention. */}
          {items.length === 0 ? (
            <Empty>Nobody has filed a report yet.</Empty>
          ) : (
            <div className="mt-4 flex flex-col gap-3">
              {items.map((i, n) => (
                <Panel key={i.report_id || n} className="p-3">
                  <div className="flex flex-wrap items-center gap-2">
                    {/* The source, not a rating — there is no sentiment in
                        this payload to colour a badge by. */}
                    <Badge tone={isTesterReport(i) ? "neutral" : "green"}>
                      {i.source ?? "unknown source"}
                    </Badge>
                    {i.status && <Badge tone="amber">{i.status}</Badge>}
                    <span className="ml-auto text-xs text-slate-400 dark:text-slate-500">
                      {i.created_at ? new Date(i.created_at).toLocaleDateString() : "—"}
                    </span>
                  </div>
                  {/* Free text a user typed. React escapes it; it is never
                      rendered as markup. */}
                  {i.description && <p className="mt-1.5 text-sm">{i.description}</p>}

                  {/* WHERE TO LOOK. The thread and turn make a report
                      reproducible — the replacement for the browser-context
                      block the old payload carried. */}
                  {(i.thread_id || i.turn_id || i.model_version) && (
                    <dl className="mt-2 flex flex-wrap gap-x-4 gap-y-1 border-t border-slate-200 pt-2 text-xs dark:border-slate-700">
                      {i.thread_id && (
                        <div className="flex gap-1">
                          <dt className="text-slate-500 dark:text-slate-400">thread</dt>
                          <dd className="font-mono text-slate-700 dark:text-slate-300">
                            {i.thread_id}
                          </dd>
                        </div>
                      )}
                      {i.turn_id && (
                        <div className="flex gap-1">
                          <dt className="text-slate-500 dark:text-slate-400">turn</dt>
                          <dd className="font-mono text-slate-700 dark:text-slate-300">
                            {i.turn_id}
                          </dd>
                        </div>
                      )}
                      {i.model_version && (
                        <div className="flex gap-1">
                          <dt className="text-slate-500 dark:text-slate-400">model</dt>
                          <dd className="font-mono text-slate-700 dark:text-slate-300">
                            {i.model_version}
                          </dd>
                        </div>
                      )}
                    </dl>
                  )}

                  {/* THE URL EXPIRES. It is a presigned link, so the panel
                      says when it dies rather than handing an operator an
                      address that will quietly stop working. New tab and
                      noreferrer, because a stored URL is still data we did
                      not author. */}
                  {i.screenshot?.url && (
                    <p className="mt-2 text-xs">
                      <a
                        className="text-sky-600 underline dark:text-sky-400"
                        href={i.screenshot.url}
                        target="_blank"
                        rel="noreferrer noopener"
                      >
                        Screenshot
                      </a>
                      {i.screenshot.expires_at && (
                        <span className="ml-2 text-slate-500 dark:text-slate-400">
                          link expires {new Date(i.screenshot.expires_at).toLocaleString()}
                        </span>
                      )}
                    </p>
                  )}
                </Panel>
              ))}
            </div>
          )}

          {/* Feedback written without a tenant is filed under the literal
              tenant `default` and never reaches a real tenant's route. An
              empty list is not proof a merchant left none. */}
          {items.length === 0 && (
            <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
              Feedback submitted without a tenant is filed separately and does
              not appear here.
            </p>
          )}
        </>
      )}
    </Card>
  );
}
